/**
 * Permission Configuration
 * Defines which features each role can access
 */

export const ROLE_PERMISSIONS = {
  admin: {
    // Dashboard
    view_dashboard: true,
    
    // Orders
    view_orders: true,
    create_orders: true,
    edit_orders: true,
    delete_orders: true,
    view_all_vendor_orders: true,
    
    // Bookings
    view_bookings: true,
    create_bookings: true,
    edit_bookings: true,
    delete_bookings: true,
    
    // Listings
    view_listings: true,
    create_listings: true,
    edit_listings: true,
    delete_listings: true,
    approve_listings: true,
    view_all_vendor_listings: true,
    
    // Vendors
    view_vendors: true,
    create_vendors: true,
    edit_vendors: true,
    delete_vendors: true,
    approve_vendors: true,
    view_vendor_commissions: true,
    edit_commissions: true,
    view_vendor_payouts: true,
    manage_subscription_plans: true,
    
    // Customers
    view_customers: true,
    create_customers: true,
    edit_customers: true,
    delete_customers: true,
    view_customer_segments: true,
    
    // Discounts
    view_discounts: true,
    create_discounts: true,
    edit_discounts: true,
    delete_discounts: true,
    
    // Marketing
    view_marketing: true,
    create_campaigns: true,
    manage_marketing: true,
    view_promotions: true,
    send_emails: true,
    send_sms: true,
    
    // Analytics
    view_analytics: true,
    view_all_vendor_analytics: true,
    
    // Content Management
    view_content: true,
    manage_categories: true,
    manage_tags: true,
    manage_collections: true,
    manage_media: true,
    manage_pages: true,
    manage_menus: true,
    manage_blog: true,
    
    // Settings
    view_settings: true,
    edit_settings: true,
    manage_users: true,
    manage_roles: true,
    view_integrations: true,
    
    // Admin-specific
    view_approvals: true,
    view_admin_reports: true
  },
  
  vendor: {
    // Dashboard
    view_dashboard: true,
    
    // Orders (own orders only)
    view_orders: true,
    create_orders: false,
    edit_orders: true,
    delete_orders: false,
    view_customer_requests: true,
    
    // Bookings (own bookings only)
    view_bookings: true,
    create_bookings: false,
    edit_bookings: true,
    delete_bookings: false,
    
    // Listings (own listings only)
    view_listings: true,
    create_listings: true,
    edit_listings: true,
    delete_listings: true,
    
    // Inventory & Shipping
    view_inventory: true,
    manage_inventory: true,
    view_shipping: true,
    manage_shipping: true,
    
    // Business Settings
    view_store_profile: true,
    edit_store_profile: true,
    view_payouts: true,
    view_vendor_financials: true,
    view_vendor_advertising: true,
    manage_operational_settings: true,
    view_security_settings: true,
    
    // Customers (own customers only)
    view_customers: true,
    create_customers: false,
    edit_customers: false,
    view_customer_segments: true,
    
    // Discounts (own store only)
    view_discounts: true,
    create_discounts: true,
    edit_discounts: true,
    delete_discounts: true,
    
    // Analytics (own store only)
    view_analytics: true,
    
    // Marketing (own store only)
    view_marketing: true,
    create_campaigns: true,
    view_promotions: true,
    send_emails: true,
    
    // Content (limited)
    manage_media: true, // Own media only
    
    // Settings (limited)
    view_settings: true,
    edit_settings: false, // Can only edit own vendor settings
    
    // Help & Messaging
    view_help: true,
    view_messages: true,
    
    // Restricted from admin features
    view_vendors: false,
    view_vendor_commissions: false,
    approve_vendors: false,
    manage_subscription_plans: false,
    view_approvals: false,
    view_admin_reports: false,
    view_integrations: false,
    manage_users: false,
    manage_roles: false
  }
};

/**
 * Check if a user has a specific permission
 * @param {Object} user - User object with role
 * @param {string} permission - Permission to check
 * @returns {boolean}
 */
export const hasPermission = (user, permission) => {
  if (!user || !user.role) return false;
  
  // If permission is 'all', allow for authenticated users
  if (permission === 'all') return true;
  
  // Admin and Super Admin have all permissions by default
  if (user.role === 'admin' || user.role === 'super_admin') return true;
  
  // Check role-based permissions
  const rolePermissions = ROLE_PERMISSIONS[user.role];
  if (!rolePermissions) return false;
  
  return rolePermissions[permission] === true;
};

/**
 * Get all permissions for a role
 * @param {string} role - Role name
 * @returns {Object}
 */
export const getRolePermissions = (role) => {
  return ROLE_PERMISSIONS[role] || {};
};
